import _ from 'lodash';
import type MarginType from '@this/domain/organization/margin_type2';
import type ListWrapperInterface from '../list_wrapper_interface';
import type { TransitJson } from '../select_repository';
import Transit from './transit';

export type TransitMoreDirection = 'prev' | 'next';

interface TransitListArgs {
  showFee?: boolean;
  package?: boolean;
  // packageType?: 'IIT' | 'DP';
  marginType?: MarginType;
  current?: string;
  changeableAir?: boolean;
  domesticAirPriceIndex?: number;
  handleChange?: (prevId: string | undefined) => void;
}
class TransitList implements ListWrapperInterface {
  private showFee: boolean;

  public package: boolean | undefined;

  // private packageType: 'IIT' | 'DP' | undefined;

  private marginType: MarginType | undefined;

  public list: Transit[];

  public currentId: string | undefined;

  public segment: number;

  public showBox: boolean;

  private newId: string | null;

  private newChangeable: boolean | null | undefined;

  public isAirChangeable: boolean | undefined;

  public domesticAirPriceIndex: number | undefined;

  private handleChange: ((prevId: string | undefined) => void) | null = null;

  private filteredAirlines: string[];

  private filteredChangeablePrice: string[];

  private filteredUpgradeSeat: boolean;

  private showConnectingAir: boolean;

  public shinkansenVacancyLoading: boolean;

  public shinkansenVacancyEnabled: boolean;

  public moreSearchEnabled: boolean;

  public moreLoading: TransitMoreDirection | null;

  public moreExhausted: { prev: boolean; next: boolean };

  public moreError: { prev: string | null; next: string | null };

  public onFetchMore: ((direction: TransitMoreDirection) => Promise<void>) | null = null;

  constructor(rawTransits?: TransitJson[], args: TransitListArgs = {}) {
    this.showFee = typeof args.showFee === 'undefined' ? true : args.showFee;
    this.package = args.package;
    // this.packageType = args.packageType;
    this.marginType = args.marginType;
    this.list = _.map(
      rawTransits,
      t =>
        new Transit(
          _.merge(t, {
            showFee: this.showFee,
            package: this.package,
            // packageType: this.packageType,
            marginType: this.marginType
          })
        )
    );
    if (!this.package) this.list = _.uniqBy(this.list, t => t.text) || [];

    let t;
    if (args.current) {
      t = this.list.find(item => {
        let price: number | null | undefined;
        if (item.air) {
          // domesticAirPriceIndexが指定されている場合は、その席種・券種の価格を使用
          if (args.domesticAirPriceIndex !== undefined && item.prices && item.prices[args.domesticAirPriceIndex]) {
            price = item.prices[args.domesticAirPriceIndex].price;
          } else {
            // フォールバック：changeable_air に基づいて価格を選択
            price = args.changeableAir ? item.changeable_price : item.unchangeable_price;
          }
        } else {
          price = item.price;
        }
        const matchKey = item.text.replace(/\n/g, '') + (price ?? 0);
        return matchKey === args.current;
      });
      // 指定運賃indexだけ制限対象のケースは便を保持し、後段のindex補正で選択可能運賃へ寄せる
      if (t && !t.isSameDayDefaultSelectable()) t = undefined;
    }

    if (!t) t = this.list.find(t => !!t.optimal && t.isSameDayDefaultSelectable());
    if (!t) {
      const selectableList = this.list.filter(item => item.isSameDayDefaultSelectable());
      if (selectableList.length > 0)
        t = args.package ? _.sortBy(selectableList, item => item.price)[0] : selectableList[0];
    }

    this.currentId = t && t.id;
    this.segment = t && t.airExists() ? 0 : 1;

    // デフォルトの券種、席種を持つpriceの順番をセット
    if (t?.prices) {
      // URLから渡されたdomesticAirPriceIndexがある場合はそれを優先使用
      if (
        args.domesticAirPriceIndex !== undefined &&
        args.domesticAirPriceIndex >= 0 &&
        args.domesticAirPriceIndex < t.prices.length
      ) {
        this.domesticAirPriceIndex = args.domesticAirPriceIndex;
      } else {
        // 既存ロジック（changeableAirに基づく推測）へフォールバック
        const priceIndex = t?.prices.findIndex(p => {
          const changeable = p.ticket_category === 'changeable';
          return args.changeableAir === changeable;
        });
        this.domesticAirPriceIndex = priceIndex >= 0 ? priceIndex : 0;
      }
      const selectedPrice = t.prices[this.domesticAirPriceIndex];
      if (selectedPrice && t.isPriceSameDayRestricted(selectedPrice)) {
        const fallbackIndex = t.prices.findIndex(p => !t.isPriceSameDayRestricted(p));
        if (fallbackIndex >= 0) this.domesticAirPriceIndex = fallbackIndex;
      }
    } else {
      this.domesticAirPriceIndex = 0;
    }

    // for CSSTransition
    this.showBox = true;
    this.newId = null;
    this.newChangeable = null;

    if (args.changeableAir !== undefined) {
      this.updateAirChangeable(args.changeableAir);
    } else {
      this.updateAirChangeable(true);
    }

    if (args.handleChange) {
      this.handleChange = (prevId: string | undefined) => {
        if (args.handleChange) args.handleChange(prevId);
      };
    }

    this.filteredAirlines = [];
    this.filteredChangeablePrice = [];
    this.filteredUpgradeSeat = true;
    this.shinkansenVacancyLoading = false;
    this.shinkansenVacancyEnabled = false;
    this.moreSearchEnabled = false;
    this.moreLoading = null;
    this.moreExhausted = { prev: false, next: false };
    this.moreError = { prev: null, next: null };
    if (this.segment === 0 && this.list.filter(t => t.airExists() && !t.connectingAirExists()).length > 0) {
      this.showConnectingAir = true;
    } else {
      this.showConnectingAir = false;
    }
  }

  firstHandleChange() {
    if (this.handleChange) this.handleChange(undefined);
  }

  find(id: string): Transit | undefined {
    if (id) return this.list.find(t => t.id === id);
    return undefined;
  }

  current(): Transit | undefined {
    return this.currentId !== undefined ? this.find(this.currentId) : undefined;
  }

  currentChangeablePrice() {
    const current = this.current();
    return current && current.changeable_price;
  }

  currentUnchangeablePrice() {
    const current = this.current();
    return current && current.unchangeable_price;
  }

  currentIndex() {
    const current = this.current();
    if (!current) return undefined;

    const i = this.list.indexOf(current);
    if (i === -1) {
      return 0;
    }
    return i;
  }

  selectedPriceOriginalIndex(id: string, index: number | undefined) {
    const transit = this.find(id);
    // transitのpricesやpricesOriginalはnullがあり得る。（新幹線やパッケージの場合）
    if (transit?.prices == null || transit?.pricesOriginal == null) {
      return undefined;
    }
    const selectedPrice = transit?.prices[index || 0];
    if (selectedPrice) {
      return transit?.pricesOriginal.indexOf(selectedPrice);
    }
    return undefined;
  }

  // 受付時間超過便・当日予約制限運賃への選択可否。UIのdisabledとは別経路（handleSelect等）からも
  // 到達し得るため、選択系の各入口で共通利用する
  canSelectSameDay(id: string, selectedDomesticAirPriceIndex?: number): boolean {
    const t = this.find(id);
    if (!t) return true;
    if (t.isSameDayUnselectable()) return false;
    if (t.prices && selectedDomesticAirPriceIndex !== undefined) {
      const selectedPrice = t.prices[selectedDomesticAirPriceIndex];
      if (selectedPrice && t.isPriceSameDayRestricted(selectedPrice)) return false;
    }
    return true;
  }

  select(id: string, changeable?: boolean) {
    this.list.forEach(t => {
      t.hovered = false;
    });

    const t = this.find(id);
    if (!t) return;

    if (!this.canSelectSameDay(id)) return;

    if (t.airExists())
      if (
        !(
          (changeable && t.changeable_price) ||
          (!changeable && t.unchangeable_price) ||
          (t.package && t.additionalPrice !== null) ||
          (t.prices && t.prices.length > 0)
        )
      )
        return;

    const prevId = this.currentId;
    this.currentId = id;
    if (this.handleChange) this.handleChange(prevId);
    if (typeof changeable === 'undefined') {
      app.render();
    } else {
      this.setAirChangeable(changeable);
    }
  }

  selectWithAnimation(
    id: string,
    changeable: boolean | undefined,
    selectedDomesticAirPriceIndex: number | undefined
  ) {
    if (!this.canSelectSameDay(id, selectedDomesticAirPriceIndex)) return;

    this.newId = id;
    this.newChangeable = changeable;
    this.domesticAirPriceIndex = this.selectedPriceOriginalIndex(id, selectedDomesticAirPriceIndex) || 0;
    app.render(); // スマホのために一度renderしてoutlineを表示しておく

    setTimeout(() => {
      this.showBox = false;
      app.render();
    }, 100); // スマホでCSSTransitionのonExitを発火させるため
  }

  handleTransitionExited() {
    this.showBox = true;
    if (this.newId && this.newChangeable !== null) this.select(this.newId, this.newChangeable);
    this.newId = null;
    this.newChangeable = null;
  }

  hasAirConnection() {
    this.list.some(t => t.segments.filter(s => s.isAir()).length >= 2);
  }

  updateAirChangeable(c: boolean) {
    if (c) {
      const t = this.current();
      if (t && t.changeable_price) this.isAirChangeable = true;
    } else {
      const t = this.current();
      if (t && t.unchangeable_price) this.isAirChangeable = false;
    }
    this.list.forEach(t => {
      t.isAirChangeable = this.isAirChangeable;
    });
  }

  setAirChangeable(c: boolean) {
    this.updateAirChangeable(c);
    app.render();
  }

  isCurrentShinkansenTooLate(): boolean {
    const current = this.current();
    return !!current && current.shinkansenTooLate();
  }

  currentShinkansenDeadline() {
    const current = this.current();
    return current && current.shinkansenDeadline();
  }

  isCurrentShinkansenUnavailable(): boolean {
    const current = this.current();
    return !!current && current.segments.some(s => s.isShinkansenUnavailable());
  }

  isCurrentAirUnavailable(unavailableTransitIds: Set<string>): boolean {
    const current = this.current();
    return !!current && unavailableTransitIds.has(current.id);
  }

  isCurrentAirTooLate(): boolean {
    const current = this.current();
    return !!current && current.airTooLate();
  }

  isCurrentSameDayUnselectable(): boolean {
    const current = this.current();
    return !!current && current.isSameDayUnselectable();
  }

  // リスト非空かつデフォルト選択可能な便が1つもない（時間超過 or 全運賃制限を含む。選択ロジック・「次へ」ガード用）
  noSameDayDefaultSelectableTransit(): boolean {
    return this.list.length > 0 && this.list.every(t => !t.isSameDayDefaultSelectable());
  }

  // リスト非空かつ全便が受付時間超過（コールアウト表示条件用）
  allSameDayTimeExceeded(): boolean {
    return this.list.length > 0 && this.list.every(t => t.isSameDayUnselectable());
  }

  includeAir() {
    return this.list.some(t => t.airExists());
  }

  includeShinkansen() {
    return this.list.some(t => t.shinkansenExists());
  }

  listBySegment(): Transit[] {
    if (this.package) return this.list;

    switch (this.segment) {
      case 0:
        return this.airList();
      case 1:
        return this.trainList();
      default:
        return this.list;
    }
  }

  airList() {
    let list = this.list.filter(t => t.airExists());
    list = list
      .map(t => {
        t.resetPricesOriginal();
        return t;
      })
      .filter(t => t.prices && t.prices.length > 0);
    if (this.showConnectingAir === true) list = list.filter(t => !t.connectingAirExists());
    if (this.filteredAirlines.length > 0) list = list.filter(t => t.isIncludeAirline(this.filteredAirlines));
    if (this.filteredChangeablePrice.length > 0) {
      const filterdList = list.map(t => {
        t.includeChangeablePrice(this.filteredChangeablePrice);
        return t;
      });
      list = filterdList.filter(t => t.prices && t.prices.length > 0);
    }
    if (this.filteredUpgradeSeat !== true) {
      const filterdList = list.map(t => {
        t.notIncludePremiumSeat();
        return t;
      });
      list = filterdList.filter(t => t.prices && t.prices.length > 0);
    }
    return list;
  }

  trainList() {
    return this.list.filter(t => !t.airExists());
  }

  showsMoreButton(): boolean {
    if (!this.moreSearchEnabled || this.package) return false;

    return this.segment === 1;
  }

  // 取れる経路が無くなるまでclick可能。click回数の上限は設けていない。
  canFetchMore(direction: TransitMoreDirection): boolean {
    if (!this.showsMoreButton()) return false;

    return !this.moreExhausted[direction];
  }

  async handleFetchMore(direction: TransitMoreDirection): Promise<void> {
    if (!this.onFetchMore || this.moreLoading) return;

    this.moreLoading = direction;
    this.moreError[direction] = null;
    app.render();
    try {
      await this.onFetchMore(direction);
    } finally {
      this.moreLoading = null;
      app.render();
    }
  }

  // 追加取得した経路を鉄道の一覧に足す。すでに表示している経路（textが同じもの）は足さない。
  appendTrainTransits(rawTransits: TransitJson[], direction: TransitMoreDirection): void {
    const added = rawTransits
      .map(
        t =>
          new Transit(
            _.merge(t, {
              showFee: this.showFee,
              package: this.package,
              marginType: this.marginType
            })
          )
      )
      .filter(t => !t.airExists())
      .filter(t => !this.list.some(e => e.text === t.text));

    if (added.length === 0) {
      this.moreExhausted[direction] = true;
      return;
    }

    const rail = _.sortBy(this.list.filter(t => !t.airExists()).concat(added), t => t.startDateTime().valueOf());
    this.list = rail.concat(this.list.filter(t => t.airExists()));
  }

  length(): number {
    return this.listBySegment().length;
  }

  // 一覧タブ（this.segment）ではなく、いま選んでいる経路の移動手段の件数。
  // タブ切替後に一覧を閉じても、アウトラインの「他◯件」が選んでいない方の件数にならないようにする。
  lengthForCurrentSelection(): number {
    if (this.package) return this.list.length;

    const current = this.current();
    if (!current) return this.length();

    return current.airExists() ? this.airList().length : this.trainList().length;
  }

  syncSegmentToCurrent(): void {
    if (this.package) return;

    const current = this.current();
    if (!current) return;

    this.segment = current.airExists() ? 0 : 1;
  }

  get isAirList(): boolean {
    return this.segment === 0;
  }

  get hasNoDirectAir(): boolean {
    return this.isAirList && this.length() === 0;
  }

  get allAirLineName(): string[] {
    return Array.from(new Set(this.list.flatMap(l => l.getAirLineName())));
  }

  isShowConnectingAir(): boolean {
    return this.showConnectingAir;
  }

  handleAirlinesChange(airlines: string[]) {
    this.filteredAirlines = airlines;
    app.render();
  }

  handleChangeablePriceChange(changeable: string[]) {
    this.filteredChangeablePrice = changeable;
    app.render();
  }

  handleFilteredUpgradeSeat(val: boolean) {
    this.filteredUpgradeSeat = val;
    app.render();
  }

  handleShowConnectingAir(val: boolean) {
    this.showConnectingAir = val;
    app.render();
  }
}

export default TransitList;
